import { useCallback, useRef, useState } from 'react';
import framework from 'utils/framework';
import LanguageLocaleUtils from 'utils/LanguageLocale';
import SignInEN from 'pages/Auth/locales/SignIn_en_US';
import SignInFR from 'pages/Auth/locales/SignIn_fr_CA';
import {
    colors, fontSizes, fontWeights, space
} from 'style/config';
import TextInput from 'components/Inputs/TextInput/TextInput';
import Button from 'components/Button/Button.f';
import buttonStyles from 'components/Button/styles';
import Divider from 'components/Divider/Divider.f';
import Icon from 'components/Icon/Icon.f';
import store from 'Store';
import Actions from 'Actions';
import FormValidator from 'utils/FormValidator';
import normalize from 'utils/credentialNormalizer';
import getCredentialLookupError from 'pages/Auth/SignIn/credentialLookupErrorMap';
import useCredentialLookup from 'pages/Auth/SignIn/hooks/useCredentialLookup';

const { wrapFunctionalComponent } = framework;

// No-op fallback so the screen can render standalone (e.g. in isolation tests)
// without a navigation handler; the real goTo is owned by the SignIn page.
const noop = () => {};
const getText = LanguageLocaleUtils.getLocalisedDataFromResource(SignInEN, SignInFR);

const REGISTER_ROUTE = '/register';

// credentialType is derived at THIS call site from the normalizer's `type`
// (credentialNormalizer keeps its committed 3-outcome shape unchanged).
const CREDENTIAL_TYPE_BY_NORMALIZER_TYPE = {
    email: 'EMAIL',
    phone: 'PHONE'
};

// SIM-9 / ACS-4133 (Figma 1466-19076): the phone-disambiguation step reuses THIS
// screen in a copy-only 'email-confirmation' mode. Default 'credential' preserves
// the original SIM-2 behavior.
const MODE_EMAIL_CONFIRMATION = 'email-confirmation';

/**
 * CredentialEntryScreen (SIM-2 / ACS-4126, SUB-TASK-2b).
 *
 * One free-text field (name='credential', BR-045/TD-018) that accepts an email
 * OR a 10-digit NANP phone. While the content is all-digits the value is live
 * auto-formatted to (xxx) xxx-xxxx with NO hard cap; the mask reverts to plain
 * text the moment an email character appears so email entry is never fought.
 *
 * Validation runs ON SUBMIT ONLY (via credentialNormalizer). Invalid input
 * short-circuits to a SINGLE generic AC-018 error with no email-vs-phone leak
 * (BR-020) and makes NO API call. Valid input derives credentialType
 * ('EMAIL'/'PHONE') from the normalizer result and calls useCredentialLookup
 * against the real OI-4 `authenticationMethods` contract, then branches:
 *   - requiresEmailConfirmation -> disambiguation (SIM-9)
 *   - securityToken (single account) -> authorization (carrying securityToken)
 *   - ERR_PHONE_NOT_FOUND / ERR_EMAIL_NOT_FOUND -> AC-019 not-found (no leak)
 *   - ERR_ACCOUNT_CLOSED -> AC-021 (backend errorMessage shown directly)
 *   - ERR_400004 (store-registered) -> "Check Your Email" verification modal
 *     when the credential was an email; inline generic error for a phone
 *   - any other code / 5xx -> AC-018 generic backend error
 * The errorCode -> presentation mapping lives in credentialLookupErrorMap so the
 * branching stays a data-driven lookup rather than a growing switch.
 * Double-submit is prevented (BR-024) via the in-flight disabled/loading button.
 *
 * Navigation is driven by the SINGLE useAuthFlow() state machine instance owned
 * by the SignIn page (pages/Auth/SignIn.f.jsx); its `goTo` is passed in as a
 * prop. This screen must NOT call useAuthFlow() itself — a second instance would
 * be independent state and goTo() here would not update the screen the page shell
 * renders against.
 *
 * @param {object} props
 * @param {(screen: string) => void} [props.goTo] - advance the shared auth-flow
 *   state machine to another screen (defaults to a no-op for standalone render).
 * @param {(context: object) => void} [props.setAuthContext] - carry the lookup
 *   result forward into the shared authContext BEFORE advancing to authorization
 *   so AuthorizationScreen/OtpEntryScreen have their inputs without a re-lookup
 *   (SIM-7 / ACS-4131, F-002). Defaults to a no-op for standalone render.
 * @param {'credential'|'email-confirmation'} [props.mode] - copy-only variant
 *   (SIM-9 / ACS-4133, Figma 1466-19076). In 'email-confirmation' mode the subtitle
 *   and field label switch to the disambiguation copy and submit is forced to
 *   credentialType EMAIL; default 'credential' is the original SIM-2 behavior.
 */
const CredentialEntryScreen = ({ goTo = noop, setAuthContext = noop, mode = 'credential' }) => {
    const isEmailConfirmation = mode === MODE_EMAIL_CONFIRMATION;
    const { lookup, isLoading } = useCredentialLookup();
    const [credential, setCredential] = useState('');
    const [error, setError] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const isSubmittingRef = useRef(false);

    // Live phone auto-format-with-revert. No validation, no error here.
    const handleChange = useCallback(event => {
        const value = event?.target?.value ?? '';
        const stripped = FormValidator.getRawPhoneNumber(value).replace(/\./g, '');
        const isDigitsOnly = stripped.length > 0 && /^\d+$/.test(stripped);

        if (isDigitsOnly) {
            // phone-in-progress: reversible mask, allow >10 (shown as raw digits)
            const display = stripped.length <= 10 ? FormValidator.getFormattedPhoneNumber(stripped) || stripped : stripped;
            setCredential(display);
        } else {
            // an email character appeared -> leave the plain text as typed
            setCredential(value);
        }
    }, []);

    const handleSubmit = useCallback(async () => {
        if (isSubmittingRef.current) {
            return;
        }

        isSubmittingRef.current = true;
        setError(null);
        setNotFound(false);

        const normalized = normalize(credential);

        // Email-confirmation mode (SIM-9, Figma 1466-19076): the disambiguation step
        // resolves an ambiguous phone by EMAIL, so the entry MUST be an actual email.
        // A phone/invalid value is rejected inline with no API call (would otherwise
        // send a non-email value tagged as EMAIL to the lookup).
        if (isEmailConfirmation ? normalized.type !== 'email' : normalized.type === 'invalid') {
            setError(getText('invalidCredential'));
            isSubmittingRef.current = false;

            return;
        }

        // In email-confirmation mode the credential is a validated email, so submit is
        // FORCED to EMAIL; otherwise map the normalizer type to the credential type.
        const credentialType = isEmailConfirmation ? 'EMAIL' : CREDENTIAL_TYPE_BY_NORMALIZER_TYPE[normalized.type];
        const isEmail = isEmailConfirmation || normalized.type === 'email';
        const response = await lookup(normalized.value, credentialType);

        if (response.error) {
            // Map the backend error code to its presentation (credentialLookupErrorMap).
            const { kind } = getCredentialLookupError(response.errorCode);

            switch (kind) {
                case 'notFound':
                    // AC-019: identical for phone and email (BR-020, no leak).
                    setNotFound(true);

                    break;
                case 'backendMessage':
                    // AC-021: the backend errorMessage differentiates closed vs
                    // blocked, so it is shown directly (per the contract doc).
                    setError(response.errorMessage || getText('genericError'));

                    break;
                case 'modalStoreRegistered':
                    // ERR_400004 store-registered: open the "Check Your Email"
                    // verification modal ONLY when the entered credential was an
                    // email (the modal shows that address). For a phone-entered
                    // credential there is no email to show, so fall back to the
                    // inline generic error.
                    if (isEmail) {
                        store.dispatch(Actions.showCheckYourEmailModal({ isOpen: true, email: normalized.value }));
                    } else {
                        setError(getText('genericError'));
                    }

                    break;
                default:
                    // 'generic' + any unrecognized code / 5xx (AC-018 / AC-034).
                    setError(getText('genericError'));
            }
        } else if (response.requiresEmailConfirmation) {
            goTo('disambiguation');
        } else if (response.securityToken) {
            // F-002: carry the lookup result already in hand into the shared
            // authContext BEFORE advancing, so AuthorizationScreen/OtpEntryScreen
            // render against real inputs and no second lookup is issued. Sourced
            // entirely from `response` + the entered credential — nothing fabricated.
            setAuthContext({
                securityToken: response.securityToken,
                availableAuthMethods: response.availableAuthMethods,
                phoneLast4Digits: response.phoneLast4Digits,
                maskedEmail: response.maskedEmail,
                enteredCredential: normalized.value,
                credentialType
            });
            goTo('authorization');
        } else {
            setError(getText('genericError'));
        }

        isSubmittingRef.current = false;
    }, [credential, lookup, goTo, setAuthContext, isEmailConfirmation]);

    return (
        <div>
            {/* Subtitle: default = FREE-shipping (Figma 270-84077, middle span bold);
                email-confirmation = the generic disambiguation prompt (Figma 1466-19076,
                no account-count leak — BR-002/003). */}
            {isEmailConfirmation ? (
                <p css={styles.subtitle}>{getText('emailConfirmSubtitle')}</p>
            ) : (
                <p css={styles.subtitle}>
                    {getText('subtitlePrefix')}
                    <strong>{getText('subtitleBold')}</strong>
                    {getText('subtitleSuffix')}
                </p>
            )}
            <TextInput
                name='credential'
                label={isEmailConfirmation ? getText('emailConfirmLabel') : getText('credentialLabel')}
                aria-label={isEmailConfirmation ? getText('emailConfirmLabel') : getText('credentialLabel')}
                placeholder={isEmailConfirmation ? getText('emailConfirmLabel') : getText('credentialPlaceholder')}
                autoOff={true}
                value={credential}
                onChange={handleChange}
            />
            {error && (
                <div
                    role='alert'
                    aria-live='assertive'
                    css={styles.error}
                >
                    <Icon
                        name='alert'
                        size={16}
                        css={styles.errorIcon}
                    />
                    <span>{error}</span>
                </div>
            )}
            {notFound && (
                <div
                    role='alert'
                    aria-live='assertive'
                    css={styles.error}
                >
                    <Icon
                        name='alert'
                        size={16}
                        css={styles.errorIcon}
                    />
                    <span>
                        {getText('notFound')} <a href={REGISTER_ROUTE}>{getText('createAccountButton')}</a>
                    </span>
                </div>
            )}
            <Button
                variant='primary'
                block={true}
                onClick={handleSubmit}
                disabled={isLoading}
                loading={isLoading}
                css={styles.continue}
            >
                {getText('continueButton')}
            </Button>
            <Divider css={styles.divider} />
            <p css={styles.newToSephora}>{getText('newToSephora')}</p>
            {/* Create Account: an <a> to /register visually styled as the
                secondary outlined pill (reuses buttonStyles). Kept as a LINK
                (not a <button>) so only "Continue" carries the button role. */}
            <a
                href={REGISTER_ROUTE}
                css={styles.createAccount}
            >
                {getText('createAccountButton')}
            </a>
        </div>
    );
};

const styles = {
    subtitle: {
        color: colors.black,
        fontSize: fontSizes.base,
        marginTop: 0,
        marginBottom: space[4]
    },
    // Inline alert (Figma "5. Errors"): warning icon + error text in
    // colors.error (#e2030f), 12px, above the field. role="alert" (ADR-002).
    error: {
        display: 'flex',
        gap: space[2],
        alignItems: 'flex-start',
        color: colors.error,
        fontSize: fontSizes.sm,
        marginTop: space[2],
        a: {
            color: colors.error,
            textDecoration: 'underline'
        }
    },
    errorIcon: {
        flexShrink: 0,
        color: colors.error
    },
    continue: {
        marginTop: space[4]
    },
    divider: {
        marginTop: space[4],
        marginBottom: space[4]
    },
    newToSephora: {
        color: colors.black,
        fontSize: fontSizes.base,
        fontWeight: fontWeights.bold,
        marginTop: 0,
        marginBottom: space[4]
    },
    createAccount: [
        buttonStyles.size(),
        ...buttonStyles.secondary,
        {
            display: 'flex',
            width: '100%'
        }
    ]
};

export default wrapFunctionalComponent(CredentialEntryScreen, 'CredentialEntryScreen');
